import React from 'react';
import { University } from '../../../types';

interface AcademicTabProps {
  university: University;
}

const AcademicTab: React.FC<AcademicTabProps> = ({ university }) => {
  // Format faculty-student ratio
  const formatFacultyRatio = (ratio: number | null | undefined) => {
    if (!ratio) return 'N/A';
    return `1:${ratio}`;
  };

  // Format online courses
  const formatOnlineCourses = (courses: string | null | undefined) => {
    if (!courses) return 'N/A';
    return courses;
  };

  return (
    <div className="tab-content" style={{ width: '100%', maxWidth: 'none', margin: 0, padding: 0 }}>
      <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Academic Life</h3>
      
      {/* Academic Overview Cards */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Faculty-Student Ratio Card */}
        <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-blue-500 dark:text-blue-400 text-2xl mr-3">
              <i className="fas fa-users"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Faculty-Student Ratio</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatFacultyRatio(university.student_faculty_ratio)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Personalized attention</p>
          </div>
        </div>

        {/* Online Courses Card */}
        <div className="bg-green-50/50 dark:bg-green-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-green-500 dark:text-green-400 text-2xl mr-3">
              <i className="fas fa-laptop"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Online Courses</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatOnlineCourses(university.offers_online_courses)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Flexible learning options</p>
          </div>
        </div>
      </div>

      {/* Academic Programs Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="text-purple-500 dark:text-purple-400 text-2xl mr-3">
            <i className="fas fa-graduation-cap"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Academic Excellence</h4>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center p-4 bg-purple-50/50 dark:bg-purple-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Undergraduate Programs</h5>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">90+</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Majors available</p>
          </div>
          <div className="text-center p-4 bg-purple-50/50 dark:bg-purple-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Graduate Programs</h5>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">200+</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Advanced degrees</p>
          </div>
          <div className="text-center p-4 bg-purple-50/50 dark:bg-purple-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Research Centers</h5>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">18</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Interdisciplinary institutes</p>
          </div>
        </div>
      </div>

      {/* Hot Majors Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="text-red-500 dark:text-red-400 text-2xl mr-3">
            <i className="fas fa-fire"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Hot Majors</h4>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-red-50/50 dark:bg-red-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Computer Science</h5>
            <p className="text-lg font-bold text-red-600 dark:text-red-400">Most Popular</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">AI & Machine Learning</p>
          </div>
          <div className="text-center p-4 bg-red-50/50 dark:bg-red-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Engineering</h5>
            <p className="text-lg font-bold text-red-600 dark:text-red-400">High Demand</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Biomedical & Electrical</p>
          </div>
          <div className="text-center p-4 bg-red-50/50 dark:bg-red-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Business</h5>
            <p className="text-lg font-bold text-red-600 dark:text-red-400">Growing Fast</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Entrepreneurship Focus</p>
          </div>
        </div>
      </div>

      {/* Academic Highlights */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 dark:text-blue-400 text-xl mr-3 mt-1">
            <i className="fas fa-star"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 dark:text-blue-300 mb-3">Academic Highlights</h4>
            <div className="grid grid-cols-2 gap-4 text-blue-800 dark:text-blue-300">
              <div>
                <p className="font-semibold mb-2">Teaching Excellence:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Small class sizes with {formatFacultyRatio(university.student_faculty_ratio)} ratio</li>
                  <li>• Nobel Prize-winning faculty</li>
                  <li>• Hands-on research opportunities</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Flexible Learning:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Online course options available</li>
                  <li>• Study abroad programs</li>
                  <li>• Interdisciplinary collaboration</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademicTab;
